import { Dynamo, MedicalDataTypes, NotificationTypes } from '../primary-record-types';

const buildQuery = (queryParams?: Record<string, string | undefined>) => {
    if (!queryParams) return '';

    const filteredQueryParams = Object.fromEntries(
        Object.entries(queryParams).filter(([, value]) => value !== undefined),
    ) as Record<string, string>;
    if (!Object.keys(filteredQueryParams).length) return '';

    return `?${new URLSearchParams(filteredQueryParams).toString()}`;
};

export default {
    api: {
        app: '/app',
        patients: '/patients',
        publicSharedReports: (shareCode: string) => `/shared-reports/${shareCode}`,
        analytics: {
            events: '/analytics/events',
        },
        users: {
            me: '/users/me',
            avatar: '/users/me/avatar',
            avatarUpdateUrl: '/users/me/avatar/update-url',
            userPromoCode: '/users/me/promo-code',
            userUTMParameters: '/users/me/utm-parameters',
            myPreferences: '/users/me/preferences',
            platform: '/users/me/platform',
            adminOrganizations: '/users/admin/organizations',
            toggleOrganizationPaidStatus: '/users/admin/organizations/toggle-paid-status',
        },
        publicOrganizations: {
            clientInvitationLogo: (organizationClientInvitationId: string) =>
                `/public-organizations/client-invitations/${organizationClientInvitationId}/organization/logo`,
        },
        errors: {
            reportError: '/errors',
        },
        organizations: {
            myOrganization: '/organizations/me',
            avatar: (organizationId: string) => `/organizations/${organizationId}/avatar`,
            logo: (organizationId: string) => `/organizations/${organizationId}/logo`,
            createOrganization: '/organizations',
            organization: (organizationId: string) => `/organizations/${organizationId}`,
            usage: {
                maxMedicalProfiles: (organizationId: string) =>
                    `/organizations/${organizationId}/usage/max-medical-profiles`,
                allowedMedicalProfiles: (organizationId: string) =>
                    `/organizations/${organizationId}/usage/allowed-medical-profiles`,
                profileMetricUsage: (profileId: string, metric: Dynamo.MedicalProfileUsageMetricType) =>
                    `/usage/medical-profiles/${profileId}/metric-types/${metric}`,
            },
            files: {
                persistToMedicalProfiles: (organizationId: string) =>
                    `/organizations/${organizationId}/files/persist-to-medical-profiles`,
                organizationMedicalProfileFileFolder: (organizationId: string, medicalProfileId: string) =>
                    `/organizations/${organizationId}/files/medical-profiles/${medicalProfileId}/organization-folder`,
            },
            visits: {
                persistToMedicalProfiles: (organizationId: string) =>
                    `/organizations/${organizationId}/visits/persist-to-medical-profiles`,
            },
            groups: {
                groups: (organizationId: string) => `/organizations/${organizationId}/groups`,
                group: (organizationId: string, groupId: string) =>
                    `/organizations/${organizationId}/groups/${groupId}`,
                bulkAddClientRecordGroups: (organizationId: string) =>
                    `/organizations/${organizationId}/groups/client-records/bulk-add`,
            },
            invitationDefinitions: {
                invitationDefinitions: (organizationId: string) =>
                    `/organizations/${organizationId}/invitation-definitions`,
                invitationDefinition: (organizationId: string, organizationInvitationDefinitionId: string) =>
                    `/organizations/${organizationId}/invitation-definitions/${organizationInvitationDefinitionId}`,
                invitationDefinitionFile: (
                    organizationId: string,
                    organizationInvitationDefinitionId: string,
                    fileId: string,
                ) =>
                    `/organizations/${organizationId}/invitation-definitions/${organizationInvitationDefinitionId}/files/${fileId}`,
            },
            userInvitations: {
                userInvitations: (organizationId: string) => `/organizations/${organizationId}/user-invitations`,
                userInvitation: (organizationId: string, organizationUserInvitationId: string) =>
                    `/organizations/${organizationId}/user-invitations/${organizationUserInvitationId}`,
                myInvitations: '/organizations/user-invitations/me',
                inviteResponse: (organizationId: string, organizationUserInvitationId: string) =>
                    `/organizations/${organizationId}/user-invitations/${organizationUserInvitationId}/response`,
                invitationFile: (organizationId: string, organizationInvitationDefinitionId: string, fileId: string) =>
                    `/organizations/${organizationId}/invitation-definitions/${organizationInvitationDefinitionId}/files/${fileId}`,
                resendInvitationEmail: (organizationId: string, userInvitationId: string) =>
                    `/organizations/${organizationId}/user-invitations/${userInvitationId}/resend-email`,
            },
            users: {
                users: (organizationId: string) => `/organizations/${organizationId}/users`,
                user: (organizationId: string, organizationUserId: string) =>
                    `/organizations/${organizationId}/users/${organizationUserId}`,
                userAvatar: (organizationId: string, organizationUserId: string) =>
                    `/organizations/${organizationId}/users/${organizationUserId}/avatar`,
                userPermissions: (organizationId: string, organizationUserId: string) =>
                    `/organizations/${organizationId}/users/${organizationUserId}/permissions`,
                userGroups: (organizationId: string, organizationUserId: string) =>
                    `/organizations/${organizationId}/users/${organizationUserId}/groups`,
            },
            clientInvitations: {
                organizationLogo: (organizationId: string, organizationClientInvitationId: string) =>
                    `/organizations/${organizationId}/client-invitations/${organizationClientInvitationId}/organization/logo`,
                organizationAvatar: (organizationId: string, organizationClientInvitationId: string) =>
                    `/organizations/${organizationId}/client-invitations/${organizationClientInvitationId}/organization/avatar`,
                myInvitations: '/organizations/client-invitations/me',
                organizationClientInvitations: (organizationId: string) =>
                    `/organizations/${organizationId}/client-invitations`,
                organizationClientInvitation: (organizationId: string, clientInvitationId: string) =>
                    `/organizations/${organizationId}/client-invitations/${clientInvitationId}`,
                organizationClientInvitationResponse: (organizationId: string, clientInvitationId: string) =>
                    `/organizations/${organizationId}/client-invitations/${clientInvitationId}/response`,
                invitationOrganizationAvatar: (organizationId: string, clientInvitationId: string) =>
                    `/organizations/${organizationId}/client-invitations/${clientInvitationId}/organization/avatar`,
                invitationOrganizationLogo: (organizationId: string, clientInvitationId: string) =>
                    `/organizations/${organizationId}/client-invitations/${clientInvitationId}/organization/logo`,
                organizationClientInvitationFile: (
                    organizationId: string,
                    organizationClientInvitationId: string,
                    fileId: string,
                ) =>
                    `/organizations/${organizationId}/client-invitations/${organizationClientInvitationId}/files/${fileId}`,
                resendInvitationEmail: (organizationId: string, clientInvitationId: string) =>
                    `/organizations/${organizationId}/client-invitations/${clientInvitationId}/resend-email`,
            },
            clientRecords: {
                clientRecords: (organizationId: string) => `/organizations/${organizationId}/client-records`,
                tableSettings: (organizationId: string) =>
                    `/organizations/${organizationId}/client-records/table-settings`,
                clientRecord: (organizationId: string, organizationClientRecordId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}`,
                reEngageClient: (organizationId: string, organizationClientRecordId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/reengage`,
                inviteClient: (organizationId: string, organizationClientRecordId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invite`,
                clientRecordInvitation: (organizationId: string, organizationClientRecordId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invitation`,
                clientRecordHistory: (organizationId: string, medicalProfileId: string) =>
                    `/organizations/${organizationId}/client-records/medical-profiles/${medicalProfileId}`,
                clientRecordAuditLogs: (organizationId: string, clientRecordId: string) =>
                    `/organizations/${organizationId}/client-records/${clientRecordId}/audit-logs`,
                clientRecordInvitationFile: (organizationId: string, clientRecordId: string, fileId: string) =>
                    `/organizations/${organizationId}/client-records/${clientRecordId}/invitation-files/${fileId}`,
                bulkAdd: (organizationId: string) => `/organizations/${organizationId}/client-records/bulk`,
                billingDetails: (organizationId: string, organizationClientRecordId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/billing-details`,
                invoiceItems: (organizationId: string, organizationClientRecordId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invoice-items`,
                invoiceItem: (organizationId: string, organizationClientRecordId: string, invoiceItemId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invoice-items/${invoiceItemId}`,
                bulkAddInvoiceItems: (organizationId: string, organizationClientRecordId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invoice-items/bulk`,
                clientInvoices: (organizationId: string, organizationClientRecordId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invoices`,
                previewInvoice: (organizationId: string, organizationClientRecordId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invoices/preview`,
                clientInvoice: (organizationId: string, organizationClientRecordId: string, invoiceId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invoices/${invoiceId}`,
                refundClientInvoice: (organizationId: string, organizationClientRecordId: string, invoiceId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invoices/${invoiceId}/refund`,
                invoiceInvoiceItems: (organizationId: string, organizationClientRecordId: string, invoiceId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invoices/${invoiceId}/invoice-items`,
                voidInvoice: (organizationId: string, organizationClientRecordId: string, invoiceId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invoices/${invoiceId}/void`,
                markInvoiceAsPaid: (organizationId: string, organizationClientRecordId: string, invoiceId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invoices/${invoiceId}/mark-paid`,
                resendInvoice: (organizationId: string, organizationClientRecordId: string, invoiceId: string) =>
                    `/organizations/${organizationId}/client-records/${organizationClientRecordId}/invoices/${invoiceId}/resend`,
                clientRecordGroups: (organizationId: string, clientRecordId: string) =>
                    `/organizations/${organizationId}/client-records/${clientRecordId}/groups`,
                clientProfileNotificationCounts: (organizationId: string) =>
                    `/organizations/${organizationId}/client-profile-notification-counts`,
            },
            profiles: {
                profiles: (organizationId: string) => `/organizations/${organizationId}/medical-profiles`,
                profile: (organizationId: string, medicalProfileId: string) =>
                    `/organizations/${organizationId}/medical-profiles/${medicalProfileId}`,
            },
            careTeamMembers: {
                careTeamMembers: (organizationId: string) => `/organizations/${organizationId}/care-team-members`,
                careTeamMember: (organizationId: string, careTeamMemberId: string) =>
                    `/organizations/${organizationId}/care-team-members/${careTeamMemberId}`,
                careTeamMemberAvatar: (organizationId: string, careTeamMemberId: string) =>
                    `/organizations/${organizationId}/care-team-members/${careTeamMemberId}/avatar`,
                careTeamMemberInsuranceCard: (
                    organizationId: string,
                    careTeamMemberId: string,
                    frontOrBack: 'front' | 'back',
                ) =>
                    `/organizations/${organizationId}/care-team-members/${careTeamMemberId}/insurance-card-image/${frontOrBack}`,
                copyCareTeamMemberToClients: (organizationId: string, careTeamMemberId: string) =>
                    `/organizations/${organizationId}/care-team-members/${careTeamMemberId}/copy-to-medical-profiles`,
            },
            paymentIntegration: {
                getSetupUrl: (organizationId: string) => `/organizations/${organizationId}/payment-integration/setup`,
                status: (organizationId: string) => `/organizations/${organizationId}/payment-integration/status`,
                getOnboardingUrl: (organizationId: string) =>
                    `/organizations/${organizationId}/payment-integration/onboarding-url/retrieve`,
                getDashboardUrl: (organizationId: string) =>
                    `/organizations/${organizationId}/payment-integration/dashboard-url`,
                brandingImage: (organizationId: string, iconOrLog: 'logo' | 'icon') =>
                    `/organizations/${organizationId}/payment-integration/branding/images/${iconOrLog}`,
                brandingImages: (organizationId: string) =>
                    `/organizations/${organizationId}/payment-integration/branding/images`,
                setupTestOrganization: (organizationId: string) =>
                    `/organizations/${organizationId}/payment-integration/setup-test`,
            },
            auditLogs: (organizationId: string) => `/organizations/${organizationId}/audit-logs`,
        },
        payment: {
            webCheckoutSession: '/payments/web-checkout-session',
            webBillingSession: '/payments/web-billing-session',
            validatePromotionCode: '/payments/validate-promotion-code',
            webProducts: '/payments/web-products',
            paymentStatus: '/payments/payment-status',
            validateRegistrationCode: '/payments/validate-registration-code',
            applyRegistrationCode: '/payments/apply-registration-code',
            myRegistrationCode: '/payments/my-registration-code',
            registrationCode: '/payments/registration-code',
        },
        medicalProfileFiles: {
            medicalProfileFile: (medicalProfileId: string, medicalProfileFileId: string) =>
                `/medical-profile-files/${medicalProfileId}/files/${medicalProfileFileId}`,
            medicalProfileFileDownloadUrl: (medicalProfileId: string, medicalProfileFileId: string) =>
                `/medical-profile-files/${medicalProfileId}/files/${medicalProfileFileId}/download-url`,
            medicalProfileFiles: (medicalProfileId: string) => `/medical-profile-files/${medicalProfileId}`,
            search: (medicalProfileId: string) => `/medical-profile-files/${medicalProfileId}/search`,
            reprocess: (medicalProfileId: string, medicalProfileFileId: string) =>
                `/medical-profile-files/${medicalProfileId}/files/${medicalProfileFileId}/reprocess`,
            pageImage: (medicalProfileId: string, medicalProfileFileId: string, page: number) =>
                `/medical-profile-files/${medicalProfileId}/files/${medicalProfileFileId}/images/page/${page}`,
            audio: (medicalProfileId: string, medicalProfileFileId: string) =>
                `/medical-profile-files/${medicalProfileId}/files/${medicalProfileFileId}/audio`,
            folder: (medicalProfileId: string, folderId: string) =>
                `/medical-profile-files/${medicalProfileId}/folders/${folderId}`,
            profileFolders: (medicalProfileId: string) => `/medical-profile-files/${medicalProfileId}/folders`,
            folderDescendants: (medicalProfileId: string, folderId: string) =>
                `/medical-profile-files/${medicalProfileId}/folders/${folderId}/immediate-descendants`,
            updateFileFolder: (medicalProfileId: string, medicalProfileFileId: string) =>
                `/medical-profile-files/${medicalProfileId}/files/${medicalProfileFileId}/parent-folder`,
        },
        ehrIntegrations: {
            oneUpHealth: {
                endpoints: {
                    endpoint: (id: number) => `/ehr-integrations/one-up-health/endpoints/${id}`,
                    search: '/ehr-integrations/one-up-health/endpoints/search',
                    image: (imageUrl: string) =>
                        `/ehr-integrations/one-up-health/image${buildQuery({
                            imageUrl,
                        })}`,
                    connect: (medicalProfileId: string) =>
                        `/ehr-integrations/one-up-health/medical-profiles/${medicalProfileId}/endpoints/connect`,
                    refresh: (medicalProfileId: string) =>
                        `/ehr-integrations/one-up-health/medical-profiles/${medicalProfileId}/refresh`,
                    automaticRefresh: (medicalProfileId: string, endpointId: number) =>
                        `/ehr-integrations/one-up-health/medical-profiles/${medicalProfileId}/endpoints/${endpointId}/automatic-refresh`,
                    refreshProgress: (medicalProfileId: string) =>
                        `/ehr-integrations/one-up-health/medical-profiles/${medicalProfileId}/refresh/progress`,
                    connectedEndpoints: (medicalProfileId: string) =>
                        `/ehr-integrations/one-up-health/medical-profiles/${medicalProfileId}/connected-endpoints`,
                    connectedEndpoint: (medicalProfileId: string, connectedEndpointId: string) =>
                        `/ehr-integrations/one-up-health/medical-profiles/${medicalProfileId}/endpoints/${connectedEndpointId}`,
                    endpointIngestionHistory: (medicalProfileId: string, connectedEndpointId: string) =>
                        `/ehr-integrations/one-up-health/medical-profiles/${medicalProfileId}/endpoints/${connectedEndpointId}/ingestions`,
                    confirmPatientMismatch: (medicalProfileId: string, connectedEndpointId: string) =>
                        `/ehr-integrations/one-up-health/medical-profiles/${medicalProfileId}/endpoints/${connectedEndpointId}/confirm-patient-mismatch`,
                    automaticRefreshStatus: (medicalProfileId: string, endpointId: string) =>
                        `/ehr-integrations/one-up-health/medical-profiles/${medicalProfileId}/endpoints/${endpointId}/automatic-refresh-status`,
                    refreshCapabilityDiscovery: (medicalProfileId: string, endpointId: string) =>
                        `/ehr-integrations/one-up-health/medical-profiles/${medicalProfileId}/endpoints/${endpointId}/refresh-capability-discovery`,
                },
            },
            fasten: {
                endpoints: {
                    connections: {
                        list: (medicalProfileId: string) => `/ehr-integrations/fasten/connections/${medicalProfileId}`,
                        disconnect: (medicalProfileId: string, orgConnectionId: string) =>
                            `/ehr-integrations/fasten/connections/${medicalProfileId}/${orgConnectionId}`,
                        refresh: (medicalProfileId: string, orgConnectionId: string) =>
                            `/ehr-integrations/fasten/connections/${medicalProfileId}/${orgConnectionId}/refresh`,
                        refreshStatus: (medicalProfileId: string, orgConnectionId: string) =>
                            `/ehr-integrations/fasten/connections/${medicalProfileId}/${orgConnectionId}/refresh/status`,
                        automaticRefreshStatus: (medicalProfileId: string, orgConnectionId: string) =>
                            `/ehr-integrations/fasten/connections/${medicalProfileId}/${orgConnectionId}/automatic-refresh-status`,
                        ingestions: (medicalProfileId: string, orgConnectionId: string) =>
                            `/ehr-integrations/fasten/connections/${medicalProfileId}/${orgConnectionId}/ingestions`,
                    },
                    connect: (medicalProfileId: string) => `/ehr-integrations/fasten/connect/${medicalProfileId}`,
                    reconnect: (medicalProfileId: string, orgConnectionId: string) =>
                        `/ehr-integrations/fasten/reconnect/${medicalProfileId}/${orgConnectionId}`,
                    tefcaSession: (medicalProfileId: string) =>
                        `/ehr-integrations/fasten/tefca-session/${medicalProfileId}`,
                    catalogSearch: '/ehr-integrations/fasten/catalog/search',
                },
            },
        },
        chats: {
            medicalProfileChat: (medicalProfileId: string, chatId: string) =>
                `/chats/medical-profiles/${medicalProfileId}/chats/${chatId}`,
            medicalProfileChats: (medicalProfileId: string) => `/chats/medical-profiles/${medicalProfileId}/chats`,
            medicalProfileChatMessages: (medicalProfileId: string, chatId: string) =>
                `/chats/medical-profiles/${medicalProfileId}/chats/${chatId}/messages`,
            medicalProfileSearch: (medicalProfileId: string) => `/chats/medical-profiles/${medicalProfileId}/search`,
            // Async endpoints
            medicalProfileChatsAsync: (medicalProfileId: string) =>
                `/chats/medical-profiles/${medicalProfileId}/chats/async`,
            medicalProfileChatMessagesAsync: (medicalProfileId: string, chatId: string) =>
                `/chats/medical-profiles/${medicalProfileId}/chats/${chatId}/messages/async`,
        },
        reports: {
            medicalProfileReport: (medicalProfileId: string) => `/reports/medical-profiles/${medicalProfileId}/reports`,
            reports: (medicalProfileId: string) => `/reports/medical-profiles/${medicalProfileId}/reports`,
            sharedReports: (medicalProfileId: string) => `/reports/shared-reports/medical-profiles/${medicalProfileId}`,
            sharedReport: (medicalProfileId: string, sharedReportId: string) =>
                `/reports/shared-reports/medical-profiles/${medicalProfileId}/shared-report/${sharedReportId}`,
        },
        auditLogs: {
            profileAuditLogsSearch: (medicalProfileId: string) =>
                `/audit-logs/medical-profiles/${medicalProfileId}/search`,
            auditLog: (medicalProfileId: string, auditLogId: string) =>
                `/audit-logs/medical-profiles/${medicalProfileId}/audit-logs/${auditLogId}`,
            auditLogCommentAvatar: (medicalProfileId: string, auditLogId: string, userId: string) =>
                `/audit-logs/medical-profiles/${medicalProfileId}/audit-logs/${auditLogId}/users/${userId}/comment-avatar`,
            auditLogComment: (medicalProfileId: string, auditLogId: string) =>
                `/audit-logs/medical-profiles/${medicalProfileId}/audit-logs/${auditLogId}/comment`,
            auditLogMessage: (medicalProfileId: string) =>
                `/audit-logs/medical-profiles/${medicalProfileId}/audit-logs`,
        },
        notes: {
            list: {
                userNotes: '/notes/my-notes',
                profileNotes: (profileId: string) => `/notes/medical-profiles/${profileId}`,
                careTeamMemberNotes: (profileId: string, careTeamMemberId: string) =>
                    `/notes/medical-profiles/${profileId}/care-team-members/${careTeamMemberId}`,
                careTeamNotes: (profileId: string) => `/notes/medical-profiles/${profileId}/care-team-notes`,
                resourceTypeNotes: (
                    profileId: string,
                    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
                ) => `/notes/medical-profiles/${profileId}/resources/${resourceCategory}`,
                resourceNotes: (
                    profileId: string,
                    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
                    resourceId: string,
                ) => `/notes/medical-profiles/${profileId}/resources/${resourceCategory}/${resourceId}`,
                resourceGroupNotes: (
                    profileId: string,
                    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
                ) => `/notes/medical-profiles/${profileId}/resources/${resourceCategory}/resource-group`,
                organizationNotes: (organizationId: string) => `/notes/organizations/${organizationId}`,
                organizationClientRecordNotes: (organizationId: string, organizationClientRecordId: string) =>
                    `/notes/organizations/${organizationId}/client-records/${organizationClientRecordId}`,
            },
            notes: '/notes',
            note: (noteId: string) => `/notes/${noteId}`,
            copyNote: (sourceMedicalProfileId: string, noteId: string) =>
                `/notes/medical-profiles/${sourceMedicalProfileId}/notes/${noteId}/copy`,
            noteImage: (noteId: string, noteImageId: string) => `/notes/${noteId}/images/${noteImageId}`,
            noteFile: (noteId: string, noteFileId: string) => `/notes/${noteId}/files/${noteFileId}`,
            noteComment: (noteId: string) => `/notes/${noteId}/comments`,
            noteUserAvatar: (noteId: string, userId: string) => `/notes/${noteId}/users/${userId}/avatar`,
            copyNoteToClients: (organizationId: string, noteId: string) =>
                `/notes/organizations/${organizationId}/notes/${noteId}/copy`,
            copyNoteToClient: (organizationId: string, noteId: string, medicalProfileId: string) =>
                `/notes/organizations/${organizationId}/notes/${noteId}/copy/medical-profiles/${medicalProfileId}`,
            sendNoteEmail: (noteId: string) => `/notes/${noteId}/send-email`,
            linkNoteToClientRecord: (noteId: string) => `/notes/${noteId}/link-to-client-record`,
        },
        medicalData: {
            fdaDrugSearch: '/medical-data/fda-drugs/search',
            elementSearch: {
                vaccineTypes: (medicalProfileId: string) =>
                    `/medical-data/medical-profiles/${medicalProfileId}/search/vaccination-types`,
            },
            visitInviteEmails: (medicalProfileId: string) =>
                `/medical-data/medical-profiles/${medicalProfileId}/visit-invite-emails`,
            counts: (medicalProfileId: string) => `/medical-data/medical-profiles/${medicalProfileId}/resource-counts`,
            filteredCounts: (medicalProfileId: string) =>
                `/medical-data/medical-profiles/${medicalProfileId}/filtered-resource-counts`,
            resources: (
                medicalProfileId: string,
                resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
                sort?: 'ascending' | 'descending',
            ) => `/medical-data/medical-profiles/${medicalProfileId}/${resourceCategory}${buildQuery({ sort })}`,
            resource: (
                medicalProfileId: string,
                resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
                resourceId: string,
            ) => `/medical-data/medical-profiles/${medicalProfileId}/${resourceCategory}/${resourceId}`,
            categoryAgnosticResource: (medicalProfileId: string, resourceId: string) =>
                `/medical-data/medical-profiles/${medicalProfileId}/resources/${resourceId}`,
            relatedResources: (
                medicalProfileId: string,
                resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
                resourceId: string,
                relatedResourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
            ) =>
                `/medical-data/medical-profiles/${medicalProfileId}/${resourceCategory}/${resourceId}/related-resources/${relatedResourceCategory}`,
            groupRelatedResources: (
                medicalProfileId: string,
                resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
                relatedResourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
            ) =>
                `/medical-data/medical-profiles/${medicalProfileId}/${resourceCategory}/resource-group/related-resources/${relatedResourceCategory}`,
            resourceImage: (
                medicalProfileId: string,
                resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
                resourceId: string,
                resourceImageId: string,
            ) =>
                `/medical-data/medical-profiles/${medicalProfileId}/${resourceCategory}/${resourceId}/images/${resourceImageId}`,
            resourceDescription: (medicalProfileId: string, resourceCategory: string, resourceId: string) =>
                `/medical-data/medical-profiles/${medicalProfileId}/${resourceCategory}/${resourceId}/description`,
            careTeamMemberResources: (
                medicalProfileId: string,
                careTeamMemberId: string,
                resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
            ) =>
                `/medical-data/medical-profiles/${medicalProfileId}/care-team-members/${careTeamMemberId}/${resourceCategory}`,
            resourceCareTeam: (
                medicalProfileId: string,
                resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
                resourceId: string,
            ) =>
                `/medical-data/medical-profiles/${medicalProfileId}/${resourceCategory}/${resourceId}/resource-care-team`,
            resourceReferenceCounts: (medicalProfileId: string) =>
                `/medical-data/medical-profiles/${medicalProfileId}/resources/reference-counts`,
            bulkUpdate: (medicalProfileId: string) =>
                `/medical-data/medical-profiles/${medicalProfileId}/resources/bulk-update`,
            updateVisitSeries: (medicalProfileId: string) =>
                `/medical-data/medical-profiles/${medicalProfileId}/visits/series-update`,
            bulkCreate: (medicalProfileId: string, resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory) =>
                `/medical-data/medical-profiles/${medicalProfileId}/${resourceCategory}/bulk-create`,
            relatedResourceSuggestions: (
                medicalProfileId: string,
                resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
                resourceId: string,
                relatedResourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
            ) =>
                `/medical-data/medical-profiles/${medicalProfileId}/${resourceCategory}/${resourceId}/related-resources/${relatedResourceCategory}/suggestions`,
            relatedResourceDiscoveries: (
                medicalProfileId: string,
                resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
                resourceId: string,
                relatedResourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory,
            ) =>
                `/medical-data/medical-profiles/${medicalProfileId}/${resourceCategory}/${resourceId}/related-resources/${relatedResourceCategory}/discoveries`,
            labDateBuckets: (medicalProfileId: string) =>
                `/medical-data/medical-profiles/${medicalProfileId}/lab/date-buckets`,
            dateLabs: (medicalProfileId: string) =>
                `/medical-data/medical-profiles/${medicalProfileId}/search/panels-for-date`,
            labHistory: (medicalProfileId: string) =>
                `/medical-data/medical-profiles/${medicalProfileId}/lab/codes/history`,
            uniqueLabCodes: (medicalProfileId: string) =>
                `/medical-data/medical-profiles/${medicalProfileId}/lab/unique-codes`,
        },
        medicalProfiles: {
            medicalProfiles: '/medical-profiles',
            medicalProfile: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}`,
            noKnownDrugAllergies: (medicalProfileId: string) =>
                `/medical-profiles/${medicalProfileId}/no-known-drug-allergies`,
            contactDetails: (medicalProfileId: string, contactDetailsId: string) =>
                `/medical-profiles/${medicalProfileId}/contacts/${contactDetailsId}`,
            avatar: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/avatar`,
            emergencyContact: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/emergency-contacts`,
            owner: {
                owner: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/owner`,
                avatar: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/owner/avatar`,
            },
            cards: {
                cards: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/cards`,
                insuranceCardImage: (medicalProfileId: string, insuranceCardId: string) =>
                    `/medical-profiles/${medicalProfileId}/insurance-cards/${insuranceCardId}/image`,
                idCardImage: (medicalProfileId: string, idCardId: string) =>
                    `/medical-profiles/${medicalProfileId}/id-cards/${idCardId}/image`,
                insuranceCard: (medicalProfileId: string, insuranceCardId: string) =>
                    `/medical-profiles/${medicalProfileId}/insurance-cards/${insuranceCardId}`,
                idCard: (medicalProfileId: string, idCardId: string) =>
                    `/medical-profiles/${medicalProfileId}/id-cards/${idCardId}`,
                insuranceCards: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/insurance-cards`,
                idCards: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/id-cards`,
            },
            inAppNotifications: {
                allProfileCounts: '/medical-profiles/in-app-notifications/counts',
                allNotifications: (medicalProfileId: string) =>
                    `/medical-profiles/${medicalProfileId}/in-app-notifications/all`,
                toggleInAppNotificationReadStatus: (medicalProfileId: string) =>
                    `/medical-profiles/${medicalProfileId}/in-app-notifications/toggle-read-status`,
                inAppNotificationAvatar: (
                    medicalProfileId: string,
                    notificationType: NotificationTypes.NotificationType,
                    entityId: string,
                    userId: string,
                ) =>
                    `/medical-profiles/${medicalProfileId}/in-app-notifications/avatar/${notificationType}/${entityId}/user/${userId}`,
            },
        },
        paymentStatus: {
            canAddProfile: '/medical-profiles/can-add-medical-profile',
            profilePaymentStatusById: (medicalProfileId: string) =>
                `/medical-profiles/${medicalProfileId}/payment-status`,
        },
        ownershipTransfer: {
            profileTransferRequest: (medicalProfileId: string) =>
                `/medical-profiles/${medicalProfileId}/transfer-requests`,
            myInboundTransferRequests: '/medical-profiles/transfer-requests/my-inbound-transfer-requests',
            createTransferRequest: (medicalProfileId: string) =>
                `/medical-profiles/${medicalProfileId}/transfer-requests`,
            revokeTransferRequest: (medicalProfileId: string) =>
                `/medical-profiles/${medicalProfileId}/transfer-requests/revoke`,
            respondToTransferRequest: (medicalProfileId: string) =>
                `/medical-profiles/${medicalProfileId}/transfer-requests/decision`,
            pendingTransferRequest: (medicalProfileId: string, transferRequestId: string) =>
                `/medical-profiles/${medicalProfileId}/pending-transfer-requests/${transferRequestId}`,
            pendingTransferRequestAvatar: (medicalProfileId: string) =>
                `/medical-profiles/${medicalProfileId}/pending-transfer-request-avatar`,
        },
        careTeamMembers: {
            careTeamMembers: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/care-team-members`,
            careTeamMember: (medicalProfileId: string, careTeamMemberId: string) =>
                `/medical-profiles/${medicalProfileId}/care-team-members/${careTeamMemberId}`,
            copy: (medicalProfileId: string, careTeamMemberId: string) =>
                `/medical-profiles/${medicalProfileId}/care-team-members/${careTeamMemberId}/copy`,
            avatar: (medicalProfileId: string, careTeamMemberId: string) =>
                `/medical-profiles/${medicalProfileId}/care-team-members/${careTeamMemberId}/avatar`,
            insuranceCard: (medicalProfileId: string, careTeamMemberId: string, frontOrBack: 'front' | 'back') =>
                `/medical-profiles/${medicalProfileId}/care-team-members/${careTeamMemberId}/insurance-card-image/${frontOrBack}`,
            relatedCareTeamMembers: (
                medicalProfileId: string,
                careTeamMemberId: string,
                relatedCareTeamMemberId?: string,
            ) =>
                `/medical-profiles/${medicalProfileId}/care-team-members/${careTeamMemberId}/related-care-team-members${
                    relatedCareTeamMemberId ? `/${relatedCareTeamMemberId}` : ''
                }`,
            resourceRelatedCareTeamMembers: (medicalProfileId: string, resourceId: string) =>
                `/medical-profiles/${medicalProfileId}/care-team-members/related-to-resource/${resourceId}`,
            resourceGroupRelatedCareTeamMembers: (medicalProfileId: string) =>
                `/medical-profiles/${medicalProfileId}/care-team-members/related-to-resources`,
        },
        collaborators: {
            careTeamMemberCollaborators: (medicalProfileId: string, careTeamMemberId: string) =>
                `/medical-profiles/${medicalProfileId}/care-team-members/${careTeamMemberId}/collaborators`,
            collaborators: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/collaborators`,
            collaborator: (medicalProfileId: string, collaboratorId: string) =>
                `/medical-profiles/${medicalProfileId}/collaborators/${collaboratorId}`,
            me: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/collaborators/me`,
            medicalProfileInvites: (medicalProfileId: string) =>
                `/medical-profiles/${medicalProfileId}/collaborators/invites`,
            medicalProfileInvite: (medicalProfileId: string, medicalProfileInviteId: string) =>
                `/medical-profiles/${medicalProfileId}/collaborators/invites/${medicalProfileInviteId}`,
            medicalProfileInvitePermissions: (medicalProfileId: string, medicalProfileInviteId: string) =>
                `/medical-profiles/${medicalProfileId}/collaborators/invites/${medicalProfileInviteId}/permissions`,
            collaboratorPermissions: (medicalProfileId: string, collaboratorId: string) =>
                `/medical-profiles/${medicalProfileId}/collaborators/${collaboratorId}/permissions`,
            myInvites: `/medical-profiles/my-invites`,
            invitedProfileAvatar: (medicalProfileId: string, inviteId: string) =>
                `/medical-profiles/${medicalProfileId}/collaborators/invites/${inviteId}/profile-avatar`,
            inviteInviterUserAvatar: (medicalProfileId: string, inviteId: string) =>
                `/medical-profiles/${medicalProfileId}/collaborators/invites/${inviteId}/inviter-avatar`,
            collaboratorAvatar: (medicalProfileId: string, collaboratorId: string) =>
                `/medical-profiles/${medicalProfileId}/collaborators/${collaboratorId}/avatar`,
            resendInviteEmail: (medicalProfileId: string, inviteId: string) =>
                `/medical-profiles/${medicalProfileId}/invites/${inviteId}/resend-email`,
            collaboratorInvoices: (medicalProfileId: string, collaboratorId: string) =>
                `/medical-profiles/${medicalProfileId}/collaborators/${collaboratorId}/invoices`,
            collaboratorInvoice: (medicalProfileId: string, collaboratorId: string, invoiceId: string) =>
                `/medical-profiles/${medicalProfileId}/collaborators/${collaboratorId}/invoices/${invoiceId}`,
        },
        directives: {
            codeStatus: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/directives/code-status`,
            poa: (medicalProfileId: string) => `/medical-profiles/${medicalProfileId}/directives/poa`,
        },
        permissions: {
            evaluatedPermissions: (medicalProfileId: string) =>
                `/medical-profiles/${medicalProfileId}/evaluated-permissions`,
        },
        files: {
            tempFiles: '/files/temp-file',
            tempFile: (tempFileId: string) => `/files/temp-file/${tempFileId}`,
        },
        featureToggles: {
            me: '/feature-toggles/me',
        },
        nativeNotifications: {
            userDevicePushTokens: '/native-notifications/user-device-push-tokens',
            removeDevicePushTokens: '/native-notifications/remove-user-device-push-tokens',
        },
    },
};
